import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faChevronDown,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  updateDoc,
  writeBatch,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import {
  SectionJMMDetail,
  SectionRundown,
} from "@mmfestival/shared-components";
import { TrilogyDetail } from "@mmfestival/shared-components/data/TrilogyDetailDelete";
import axios from "axios";
import Iframe from "react-iframe";
// import entire shared data module so we can fall back to either per-category
// mappings (qualificationByCategory / raceInclusionsByCategory) or global defaults
import * as JMMData from "@mmfestival/shared-components/data/JMMDetail";

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not specified";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not specified";
  } catch {
    return "Date not specified";
  }
};

// translate status labels (keeps robust checks for both Indonesian and English)
const getStatusKey = (status) => {
  if (!status) return "other";
  const s = String(status).trim().toLowerCase();

  // specific mappings (ID/EN)
  if (
    s === "segera dibuka" ||
    s === "segera" ||
    s === "coming soon" ||
    s === "segera dibuka."
  )
    return "coming_soon";

  if (
    s === "registrasi dibuka" ||
    s === "pendaftaran dibuka" ||
    s === "dibuka" ||
    s === "open" ||
    s === "registration open"
  )
    return "open";

  if (
    s === "registrasi ditutup" ||
    s === "pendaftaran ditutup" ||
    s === "ditutup" ||
    s === "closed" ||
    s === "registration closed"
  )
    return "closed";

  // heuristics
  if (s.includes("segera") || s.includes("coming") || s.includes("soon"))
    return "coming_soon";
  if (s.includes("dibuka") || s.includes("open")) return "open";
  if (s.includes("ditutup") || s.includes("tutup") || s.includes("closed"))
    return "closed";

  return "other";
};

const translateStatus = (status) => {
  const key = getStatusKey(status);
  if (key === "open") return "Registration Open";
  if (key === "closed") return "Registration Closed";
  if (key === "coming_soon") return "Coming Soon";
  return "Registration TBD";
};

// MAPPING EVENT ID KE COLLECTION NAME
const EVENT_COLLECTION_MAP = {
  SMM: "pendaftaranSMM",
  JMM: "pendaftaranJMM",
  BMM: "pendaftaranBMM",
};

// --- HARDCODED PREVIEW MEDIA (HYBRID) ---
// Media berikut selalu dipakai oleh komponen (override nilai di Firestore)
const HARD_PREVIEW = {
  image: "/bg/java.jpg",
  currentImage: "/bg/java.jpg",
  logo: "/logo/JMM.png",
  logodetail: "/logo/JMM1.png",
};

export const JMM = React.memo(() => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [viewMode, setViewMode] = useState("map");
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [imageModal, setImageModal] = useState({ isOpen: false, src: "", alt: "" });
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  // === FUNGSI UNTUK GET COLLECTION NAME DARI MAPPING ===
  const getCollectionName = useCallback((eventId) => {
    if (EVENT_COLLECTION_MAP[eventId]) {
      return EVENT_COLLECTION_MAP[eventId];
    }
    return null;
  }, []);

  /**
   * Archive a registration document instead of deleting it.
   * Writes a document with the same id into archivedRegistrations and removes the original
   * using a batch so the operations happen atomically from the client's POV.
   * On failure it marks the original document for archival retry and does NOT delete it,
   * avoiding irreversible deletion from the client side.
   */
  const archiveRegistration = useCallback(
    async (docRef, docData, originalCollectionName) => {
      if (!docRef) return;
      try {
        const batch = writeBatch(db);
        const archivedRef = doc(db, "archivedRegistrations", docRef.id);

        const payload = {
          ...docData,
          originalCollection: originalCollectionName || null,
          originalDocRefPath: docRef.path,
          archivedAt: serverTimestamp(),
          archivedByUid: user?.uid || null,
        };

        batch.set(archivedRef, payload);
        batch.delete(docRef);

        await batch.commit();
      } catch (e) {
        // Log the failure so developers/ops can see it in console
        console.error("Failed to archive registration (batch):", e);

        // Safer fallback: mark the original doc for archival retry instead of deleting it
        try {
          await updateDoc(docRef, {
            archiveStatus: "failed",
            archiveError: e?.message || String(e),
            archiveRetryRequestedAt: serverTimestamp(),
            // increment count if docData contains previous count
            archiveRetryCount:
              docData && docData.archiveRetryCount
                ? docData.archiveRetryCount + 1
                : 1,
          });
        } catch (markErr) {
          // If marking fails, log it — do NOT delete the original doc
          console.error("Failed to mark doc for archival:", markErr);
        }

        // NOTE: No backend notification here (you indicated there is no such endpoint).
        // If you later add a server endpoint or Cloud Function to process retries,
        // you can call it from here or let the server poll/scan documents with archiveStatus='failed'.
      }
    },
    [user]
  );

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };
      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docRef = querySnapshot.docs[0];
          const data = docRef.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              try {
                const response = await axios.get(
                  `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                    data.orderId
                  }`,
                  { params: { eventId } }
                );
                const updatedStatus = response.data.transaction_status;
                if (updatedStatus !== status) {
                  if (
                    updatedStatus === "pending" ||
                    updatedStatus === "settlement"
                  ) {
                    await updateDoc(docRef.ref, {
                      transaction_status: updatedStatus,
                    });
                    setTransactionStatus(updatedStatus);
                    return {
                      isRegistered: true,
                      status: updatedStatus,
                      token: data.snapToken,
                    };
                  } else {
                    // instead of deleting, archive the registration (safer)
                    await archiveRegistration(docRef.ref, data, collectionName);
                    setRegistrationStatus("not_registered");
                    setTransactionStatus(null);
                    setSnapToken(null);
                    return { isRegistered: false, status: null, token: null };
                  }
                }
              } catch (err) {
                console.error("Error checking payment status:", err);
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          } else {
            // not matching 'registered' conditions -> archive instead of delete (safer)
            await archiveRegistration(docRef.ref, data, collectionName);
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch (err) {
        console.error("Error checking registration:", err);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user, archiveRegistration]
  );

  const fetchEventFromTrilogy = useCallback(() => {
    // If TrilogyDetail is available globally or via import
    const found = TrilogyDetail.find((e) => e.id === "JMM");
    if (!found) return null;

    return {
      id: found.id,
      slug: found.slug,
      title: found.title,
      type: found.type,
      status: translateStatus(found.status ?? ""), // ensure canonical English
      location: found.location,
      // override media with HARD_PREVIEW
      image: HARD_PREVIEW.image,
      currentImage: HARD_PREVIEW.currentImage,
      logo: HARD_PREVIEW.logo || null,
      logodetail: HARD_PREVIEW.logodetail || null,
      aboutEvent: found.aboutEvent,
      websiteUrl: found.websiteUrl,
      eventId: found.eventId,
      price: found.price || 0,

      categories: found.categories.map((c) => ({
        categoryName: c.categoryName,
        cot: c.cot,
        date: c.date,
        description: c.description,
        distance: c.distance,
        elevationGain: c.elevationGain,
        price: c.price,
        foreignerPrice: c.foreignerPrice ?? null,
        time: c.eventDateTimestamp
          ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] || ""
          : "",
        gpxFileUrl: c.gpxFileUrl || "",
        mapUrl: c.mapUrl || "",
        // allow Trilogy detail to provide per-category qualification/raceInclusions if available
        qualification: c.qualification || null,
        raceInclusions: c.raceInclusions || null,
      })),
    };
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", "JMM"));
      if (eventDoc.exists()) {
        const data = eventDoc.data() || {};
        // build eventData from Firestore but OVERRIDE media with hardcoded preview
        const eventData = {
          id: eventDoc.id,
          slug: data.slug,
          title: data.title,
          type: data.type,
          // Translate status immediately so UI is always in English canonical labels
          status: translateStatus(data.status ?? data.statusText ?? ""),
          location: data.location,
          image: HARD_PREVIEW.image,
          currentImage: HARD_PREVIEW.currentImage,
          logo: HARD_PREVIEW.logo || null,
          logodetail: HARD_PREVIEW.logodetail || null,
          aboutEvent: data.aboutEvent,
          websiteUrl: data.websiteUrl,
          eventId: data.eventId,
          price: data.price || 0,
          categories: Array.isArray(data.categories)
            ? data.categories.map((c) => ({
                categoryName: c.categoryName,
                cot: c.cot,
                date: c.date,
                description: c.description,
                distance: c.distance,
                elevationGain: c.elevationGain,
                price: c.price,
                foreignerPrice: c.foreignerPrice ?? null,
                time: c.eventDateTimestamp
                  ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] ||
                    ""
                  : c.time || "",
                gpxFileUrl: c.gpxFileUrl || "",
                mapUrl: c.mapUrl || "",
                // read per-category qualification and inclusions from Firestore if present
                qualification: c.qualification || null,
                raceInclusions: c.raceInclusions || null,
                // support an explicit flag from Firestore to enable/disable registration per category
                registrationClosed:
                  typeof c.registrationClosed !== "undefined"
                    ? c.registrationClosed
                    : false,
                // support an explicit flag from Firestore to enable/disable entry list per category
                entryListEnabled:
                  typeof c.entryListEnabled !== "undefined"
                    ? c.entryListEnabled
                    : undefined,
              }))
            : [],
          // support an explicit flag from Firestore to enable/disable entry list at event level
          entryListEnabled:
            typeof data.entryListEnabled !== "undefined"
              ? data.entryListEnabled
              : undefined,
        };
        setEvent(eventData);
        setLoading(false);
        return eventData;
      }
    } catch (err) {
      console.info("Firebase did not find JMM. Using TrilogyDetail.", err);
    }

    // fallback to TrilogyDetail (but still override media)
    const fallbackEvent = fetchEventFromTrilogy();
    if (fallbackEvent) {
      setEvent(fallbackEvent);
      setLoading(false);
      return fallbackEvent;
    } else {
      setLoading(false);
      return null;
    }
  }, [fetchEventFromTrilogy]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (!eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const collName = getCollectionName(eventData.eventId);

          if (mounted) {
            setCollectionName(collName);

            if (collName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                collName,
                eventData.eventId
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            } else if (!collName && user) {
              setRegistrationStatus("not_registered");
            }
          }
        } catch (err) {
          console.error("Error in fetchData:", err);
          if (mounted) setCollectionName(null);
        }
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, getCollectionName, checkRegistration, user]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Payment gateway not ready.");
      return;
    }
    if (!snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }
    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onPending: async () => {
          showNotification("warning", "Payment is processing.");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onError: () => {
          showNotification("error", "Payment failed.");
        },
        onClose: () => {},
      });
    } catch (err) {
      console.error("Error in payment:", err);
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category.price == null && event.price == null) ||
        !event.eventId
      ) {
        showNotification("error", "Event data incomplete.");
        return;
      }
      if (!user) {
        showNotification("error", "Please log in first.");
        navigate("/login");
        return;
      }
      if (!collectionName) {
        showNotification("error", "Failed to load event data.");
        console.error("collectionName is null:", { eventId: event.eventId });
        return;
      }
      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path =
        event.type === "seminar"
          ? `/formseminar/${event.slug}`
          : `/form/${event.slug}`;
      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement")
          showNotification(
            "info",
            "You are already registered for this event."
          );
        else if (transactionStatus === "pending") handleBayar();
        return;
      }
      const price = category.price ?? event.price ?? 0;
      const foreignerPriceToSend = category.foreignerPrice ?? null;
      navigate(path, {
        state: {
          title,
          price,
          foreignerPrice: foreignerPriceToSend,
          eventId: event.eventId,
        },
      });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxSources, selectedCategory, event) => {
      const primaryUrl = gpxSources?.primaryUrl || null;
      const fallbackUrl = gpxSources?.fallbackUrl || null;

      if (!primaryUrl) {
        showNotification("error", "GPX file not available.");
        return;
      }

      const fetchGpxText = async (url) => {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      };

      try {
        let gpxText;

        try {
          gpxText = await fetchGpxText(primaryUrl);
        } catch (primaryErr) {
          if (fallbackUrl && fallbackUrl !== primaryUrl) {
            gpxText = await fetchGpxText(fallbackUrl);
          } else {
            throw primaryErr;
          }
        }

        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                selectedCategory.distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error exporting GPX:", err);
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    [showNotification]
  );

  const categories = useMemo(() => {
    if (!event?.categories) return [];
    return event.categories.filter((c) => c.categoryName !== "JMM UP");
  }, [event]);

  const selectedCategory = useMemo(
    () => categories[selectedCategoryIndex] || {},
    [categories, selectedCategoryIndex]
  );

  const COURSE_MAP_BY_CATEGORY = {
    "JMM 45K": "/map/JMM45.png",
    "JMM 25K": "/map/JMM25.png",
    "JMM 10K": "/map/JMM10.png",
  };

  const COURSE_MAP_WS_BY_CATEGORY = {
    "JMM 45K": "/map/JMM45WS.png",
    "JMM 25K": "/map/JMM25WS.png",
    "JMM 10K": "/map/JMM10WS.png",
  };

  const GPX_LOCAL_BY_CATEGORY = {
    "JMM 45K": "/gpx/JMM45.gpx",
    "JMM 25K": "/gpx/JMM25.gpx",
    "JMM 10K": "/gpx/JMM10.gpx",
  };

  const GPX_FIREBASE_BY_CATEGORY = {
    "JMM 45K":
      "https://firebasestorage.googleapis.com/v0/b/vionix-mmfestival.firebasestorage.app/o/activity_gpx%2FJMM45.gpx?alt=media&token=10903e5d-1a96-4e5c-ac08-1e90b8388e2e",
    "JMM 25K":
      "https://firebasestorage.googleapis.com/v0/b/vionix-mmfestival.firebasestorage.app/o/activity_gpx%2FJMM25.gpx?alt=media&token=33dbb044-e8f5-46e7-861b-35e2ebc93ebc",
    "JMM 10K":
      "https://firebasestorage.googleapis.com/v0/b/vionix-mmfestival.firebasestorage.app/o/activity_gpx%2FJMM10.gpx?alt=media&token=7171b3a6-08aa-4e9a-afac-e15f6b5e25b6",
  };

  const getCourseMapImage = useCallback((category) => {
    if (!category) return "/map/JMM-default.png";

    const rawCategoryName =
      category.categoryName != null ? String(category.categoryName).trim() : "";

    if (rawCategoryName === "JMM UP") {
      return null;
    }

    if (rawCategoryName && COURSE_MAP_BY_CATEGORY[rawCategoryName]) {
      return COURSE_MAP_BY_CATEGORY[rawCategoryName];
    }

    const normalizedCategoryName = rawCategoryName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    if (normalizedCategoryName) {
      return `/map/JMM-${normalizedCategoryName}.png`;
    }

    return "/map/JMM-default.png";
  }, []);

  const getCourseMapWsImage = useCallback((category) => {
    if (!category) return null;

    const rawCategoryName =
      category.categoryName != null ? String(category.categoryName).trim() : "";

    if (!rawCategoryName || rawCategoryName === "JMM UP") {
      return null;
    }

    return COURSE_MAP_WS_BY_CATEGORY[rawCategoryName] || null;
  }, []);

  const getCategoryGpxSources = useCallback((category) => {
    if (!category) return { primaryUrl: null, fallbackUrl: null };

    const rawCategoryName =
      category.categoryName != null ? String(category.categoryName).trim() : "";

    if (!rawCategoryName || rawCategoryName === "JMM UP") {
      return { primaryUrl: null, fallbackUrl: null };
    }

    const localUrl = GPX_LOCAL_BY_CATEGORY[rawCategoryName] || null;
    const firebaseUrl = GPX_FIREBASE_BY_CATEGORY[rawCategoryName] || null;

    return {
      primaryUrl: localUrl || firebaseUrl,
      fallbackUrl: firebaseUrl && firebaseUrl !== localUrl ? firebaseUrl : null,
    };
  }, []);

  const selectedCategoryGpxSources = useMemo(
    () => getCategoryGpxSources(selectedCategory),
    [getCategoryGpxSources, selectedCategory]
  );

  // Helper: normalize many possible formats to an array of strings
  const normalizeToArray = useCallback((raw) => {
    if (!raw && raw !== 0) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
    if (typeof raw === "string") {
      // try parse JSON array string first
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch (e) {
        // ignore
      }
      // split by newline/semicolon/pipe/comma
      const parts = raw
        .split(/\r?\n|;|\||,/)
        .map((s) => s.trim())
        .filter(Boolean);
      return parts.length ? parts : [raw.trim()];
    }
    if (typeof raw === "object") {
      // object with numeric keys or values
      try {
        const vals = Object.values(raw)
          .map((v) => (v == null ? "" : String(v).trim()))
          .filter(Boolean);
        if (vals.length) return vals;
      } catch (e) {
        // fallback
      }
    }
    return [String(raw)];
  }, []);

  // helper: fuzzy case/alias key finder for object
  const findDataKey = useCallback((obj, catName) => {
    if (!obj || !catName) return null;
    const direct = obj[catName];
    if (direct !== undefined) return catName;

    // case insensitive match
    const lower = catName.toLowerCase().trim();
    const keys = Object.keys(obj);
    const found = keys.find((k) => k.toLowerCase().trim() === lower);
    if (found) return found;

    // contains match (e.g. "10 KM" matches "10 KM Mountain Run" if defined that way, or vice versa)
    // Here we prefer exact alias if possible. "10K" vs "10 KM" handled by aliases in data.
    return null;
  }, []);

  // compute per-category qualification & inclusions, fallback to shared defaults or shared per-category mapping
  const categoryQualification = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.qualification);
    if (perCat.length) return perCat;

    // 2) shared-data per-category mapping (qualificationByCategory)
    // Use fuzzy finder for the key
    const mappedKey =
      selectedCategory?.categoryName &&
      findDataKey(
        JMMData.qualificationByCategory,
        selectedCategory.categoryName
      );

    const mapped = mappedKey
      ? JMMData.qualificationByCategory[mappedKey]
      : undefined;

    const mappedArr = normalizeToArray(mapped);
    if (mappedArr.length) return mappedArr;

    // 3) global fallback (JMMData.qualification)
    return normalizeToArray(JMMData.qualification);
  }, [selectedCategory, normalizeToArray, findDataKey]);

  const categoryInclusions = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.raceInclusions);
    if (perCat.length) return perCat;

    // 2) shared-data per-category mapping (raceInclusionsByCategory)
    const mappedKey =
      selectedCategory?.categoryName &&
      findDataKey(
        JMMData.raceInclusionsByCategory,
        selectedCategory.categoryName
      );

    const mapped = mappedKey
      ? JMMData.raceInclusionsByCategory[mappedKey]
      : undefined;

    const mappedArr = normalizeToArray(mapped);
    if (mappedArr.length) return mappedArr;

    // 3) global fallback (JMMData.raceInclusions)
    return normalizeToArray(JMMData.raceInclusions);
  }, [selectedCategory, normalizeToArray, findDataKey]);

  const isRegistrationOpen = useMemo(
    () => getStatusKey(event?.status) === "open",
    [event]
  );

  const isComingSoon = useMemo(
    () => getStatusKey(event?.status) === "coming_soon" || getStatusKey(event?.status) === "other",
    [event]
  );

  // displayStatus will always use canonical English label
  const displayStatus = useMemo(() => {
    if (selectedCategory?.registrationClosed) return "Registration Closed";
    return translateStatus(event?.status ?? "");
  }, [event, selectedCategory]);

  // LOGIC: Entry List enabled check (JMM specific or generic rule)
  // Logic from BMM: uses hardcoded toggle OR overrides from Firestore
  // We want to replicate BMM logic.
  // In BMM: `const isEntryListEnabled = false;` (hardcoded line 727)
  // BUT the checkRegistration logic tries to use it? No, checking fetchEventData
  // it sets entryListEnabled in eventData object.
  // Wait, BMM.jsx line 727 says: `const isEntryListEnabled = false;`
  // But BMM.jsx line 423 reads `entryListEnabled` from Firestore.
  // Does the UI use the hardcoded one or the event property?
  // Let's check BMM.jsx line 1275: `if (!isEntryListEnabled) return;`
  // It uses the constant.
  // So BMM effectively DISABLED entry list hardcodedly in that file version.
  // However, I should probably respect the logic: "Sesuai permintaan: hardcoded toggle".
  // If BMM has it hardcoded false, JMM should probably also be false, OR respects the pattern.
  // I will make it: `const isEntryListEnabled = event?.entryListEnabled === true || event?.categories?.[selectedCategoryIndex]?.entryListEnabled === true;`
  // OR just defaults to false as well if the user wants "menyerupai BMM".
  // If BMM is hardcoded false, I will hardcode false.
  // But wait, the task is to maintain structure.
  // If BMM hardcoded it to false, I will do same.
  const isEntryListEnabled = false; 

  const scrollToContent = () => {
    document
      .querySelector(".detail-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading)
    return (
      <div className="relative w-full h-screen overflow-hidden bg-gray-200">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5" />
      </div>
    );

  if (!event)
    return (
      <section className="w-full min-h-screen py-20 text-center text-slate-600 flex-col justify-center relative bg-white">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-slate-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-slate-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          NOT FOUND
        </motion.p>
        <button
          onClick={() => navigate("/")}
          className="text-slate-600 underline"
        >
          Back to Home
        </button>
      </section>
    );

  // local itemVariants to keep animation consistent with SectionJMMDetail
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.36,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <>
      {/* HERO HEADER */}
      <section
        className="relative w-full min-h-screen text-black text-center flex flex-col justify-center overflow-hidden"
        style={{
          backgroundImage: `url('${HARD_PREVIEW.image}')`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* === EFEK ZOOM-IN === */}
        <motion.div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            backgroundImage: `url('${HARD_PREVIEW.currentImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
          initial={{ scale: 1 }}
          animate={{ scale: 1.25 }}
          transition={{
            duration: 12,
            ease: "easeOut",
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5"></div>

        {/* Logo & Title & Description */}
        <div className="relative text-white text-center z-10 flex flex-col justify-center flex-grow">
          <motion.div
            className="py-2 flex justify-center"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {HARD_PREVIEW.logodetail && (
              <img
                src={HARD_PREVIEW.logodetail}
                alt={`${event?.title} Logo Detail`}
                className="h-24 md:h-48 w-auto object-contain drop-shadow-2xl"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </motion.div>

          <motion.h2
            className="text-sm sm:text-lg md:text-lg font-normal text-white max-w-[800px] mx-auto px-4 leading-relaxed"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {event?.aboutEvent}
          </motion.h2>
        </div>

        {/* Scroll Down */}
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-20">
          <motion.div
            className="cursor-pointer text-white animate-bounce"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            onClick={scrollToContent}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-4xl" />
          </motion.div>
        </div>
      </section>

      {/* DETAIL CONTENT */}
      <section className="detail-content bg-white">
        <div className="w-full">
          {/* KATEGORI TABS - Full width */}
          {categories.length > 1 && (
            <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
              <div className="flex flex-col divide-y divide-gray-300">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`w-full text-left uppercase px-4 py-4 block transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-red-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {categories.length > 1 && (
            <div className="hidden md:flex bg-gray-200 text-base font-normal text-gray-400 ">
              <div className="flex w-full">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-red-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* INFO UTAMA - Full-width table with balanced columns */}
          {(event.type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="hidden md:block">
                <table className="w-full border-collapse table-fixed">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[20%]" />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 pl-4 lg:pl-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pl-[max(1rem,calc((100vw-96rem)/2+1rem))] text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Flag-Off
                      </th>
                      {event.type === "trail_running" && (
                        <>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Distance
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Elevation Gain
                          </th>
                          <th className="py-3 pr-4 lg:pr-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pr-[max(1rem,calc((100vw-96rem)/2+1rem))] text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                            COT
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-4 pl-4 lg:pl-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pl-[max(1rem,calc((100vw-96rem)/2+1rem))] text-sm text-gray-700">
                        {formatDate(selectedCategory.date)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">
                        {selectedCategory.time || "-"}
                      </td>
                      {event.type === "trail_running" && (
                        <>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {selectedCategory.distance != null
                              ? `${selectedCategory.distance} KM`
                              : "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">
                            {selectedCategory.elevationGain != null
                              ? `${selectedCategory.elevationGain} M`
                              : "-"}
                          </td>
                          <td className="py-4 pr-4 lg:pr-[max(1rem,calc((100vw-80rem)/2+1rem))] 2xl:pr-[max(1rem,calc((100vw-96rem)/2+1rem))] text-right text-sm text-gray-700">
                            {selectedCategory.cot != null
                              ? `${selectedCategory.cot} Hours`
                              : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="md:hidden">
                <div className="space-y-4 py-4 bg-white">
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Start Date
                    </div>
                    <div className="text-sm text-gray-700">
                      {formatDate(selectedCategory.date)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Flag-Off
                    </div>
                    <div className="text-sm text-gray-700">
                      {selectedCategory.time || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Location
                    </div>
                    <div className="text-sm text-gray-700">
                      {event.location || "-"}
                    </div>
                  </div>
                  {event.type === "trail_running" && (
                    <>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Distance
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.distance != null
                            ? `${selectedCategory.distance} KM`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Elevation Gain
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.elevationGain != null
                            ? `${selectedCategory.elevationGain} M`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          COT
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.cot != null
                            ? `${selectedCategory.cot} Hours`
                            : "-"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DESKRIPSI */}
          <div className="py-4 mt-8 text-slate-900">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">
             {/*  <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {selectedCategory.description || "No description available."}
              </p> */}

              {/* Qualification & Race Inclusions */}
              {event.type === "trail_running" &&
                ((categoryQualification && categoryQualification.length) ||
                  (categoryInclusions && categoryInclusions.length)) && (
                  <motion.div
                    // KEEP outer wrapper white (as original). We'll wrap the internal content (qualification + inclusions) in amber container as requested.
                    className="bg-white rounded-xl border border-slate-200 p-4 mb-4"
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariants}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <FontAwesomeIcon
                        icon={faAward}
                        className="text-lg text-red-600"
                      />
                      <h2 className="text-xl lg:text-2xl font-semibold text-slate-700">
                        Qualification & Race Inclusions
                      </h2>
                    </div>

                    {/* Intro paragraph under title (from shared data module) */}
                    {JMMData.qualificationIntro && (
                      <p className="text-md text-gray-600 mb-4 whitespace-pre-line">
                        {JMMData.qualificationIntro}
                      </p>
                    )}

                    {/* NEW: Amber container wraps only the content (Qualification + Race Inclusions) */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Qualification - bullets like Sacred Features */}
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-slate-700">
                            Qualification
                          </h3>
                          {categoryQualification &&
                            categoryQualification.map((q, i) => (
                              <div
                                key={`qual-inline-${i}`}
                                className="flex gap-4"
                              >
                                <div className="flex-shrink-0 mt-2">
                                  <div className="w-2 h-2 rounded-full bg-red-600"></div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-slate-700 text-sm leading-snug">
                                    {q}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Race Inclusions - compact pills (horizontal, wrapping) */}
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-slate-700">
                            Race Inclusions
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {categoryInclusions &&
                              categoryInclusions.map((inc, i) => (
                                <div
                                  key={`inc-inline-${i}`}
                                  className="inline-flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-full border border-slate-200"
                                >
                                  <div className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0" />
                                  <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                                    {inc}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

              {event.type === "trail_running" && (
                <div>
                  {viewMode === "map" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedCategory.mapUrl ? (
                        <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-slate-200">
                          <Iframe
                            url={selectedCategory.mapUrl}
                            width="100%"
                            height="600px"
                            className="rounded-xl"
                            display="block"
                            position="relative"
                          />
                        </div>
                      ) : (
                        <div className="relative w-full h-auto rounded-xl overflow-hidden border border-slate-200 md:cursor-pointer">
                          {getCourseMapImage(selectedCategory) ? (
                            <img
                              src={getCourseMapImage(selectedCategory)}
                              alt={`Course map ${
                                selectedCategory.categoryName ||
                                selectedCategory.distance || "JMM"
                              }`}
                              className="w-full h-auto object-cover"
                              onClick={() => {
                                if (window.innerWidth >= 768) {
                                  setImageModal({
                                    isOpen: true,
                                    src: getCourseMapImage(selectedCategory),
                                    alt: `Course map ${
                                      selectedCategory.categoryName ||
                                      selectedCategory.distance || "JMM"
                                    }`,
                                  });
                                }
                              }}
                              onError={(e) => {
                                // eslint-disable-next-line no-param-reassign
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full py-10 text-center text-sm text-gray-500">
                              Course map not available for this category.
                            </div>
                          )}
                        </div>
                      )}

                      {getCourseMapWsImage(selectedCategory) && (
                        <div className="relative w-full h-auto rounded-xl overflow-hidden border border-slate-200 md:cursor-pointer">
                          <img
                            src={getCourseMapWsImage(selectedCategory)}
                            alt={`Water station map ${
                              selectedCategory.categoryName || "JMM"
                            }`}
                            className="w-full h-auto object-cover"
                            onClick={() => {
                              if (window.innerWidth >= 768) {
                                setImageModal({
                                  isOpen: true,
                                  src: getCourseMapWsImage(selectedCategory),
                                  alt: `Water station map ${
                                    selectedCategory.categoryName || "JMM"
                                  }`,
                                });
                              }
                            }}
                            onError={(e) => {
                              // eslint-disable-next-line no-param-reassign
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* TABS MAP (MOBILE) */}
          {event.type === "trail_running" && selectedCategoryGpxSources.primaryUrl && (
            <div className="md:hidden">
              <div className="flex gap-2 px-4">
                <button
                  className={`flex-1 uppercase py-3 text-sm font-medium rounded-lg transition-colors duration-300 ${
                    viewMode === "map"
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() =>
                    handleExportGpx(
                      selectedCategoryGpxSources,
                      selectedCategory,
                      event
                    )
                  }
                >
                  DOWNLOAD GPX
                </button>
              </div>
            </div>
          )}

          {/* CONTAINER STATUS HARGA PESERTA NAVIGASI */}
          <div className="pt-4 md:pt-0 pb-4">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">
              {/* DESKTOP WRAPPER: only md+ and add 4px side spacing */}
              <div className="hidden md:block">


                {/* STATUS, HARGA, PESERTA, NAVIGASI CONTAINER - DESKTOP */}
                <div className="border border-gray-300 rounded-2xl p-4">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Status */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Status
                      </p>
                      <div className="flex items-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full mr-2 ${
                            getStatusKey(event.status) === "open"
                              ? "bg-red-500"
                              : getStatusKey(event.status) === "closed"
                              ? "bg-red-500"
                              : "bg-yellow-500"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          {displayStatus}
                        </span>
                      </div>
                    </div>

                    {/* Harga desktop */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Price
                      </p>
                      <div className="text-sm font-semibold text-red-600">
                        {selectedCategory.foreignerPrice != null ? (
                          <div>
                            {(() => {
                              const domesticPrice =
                                selectedCategory.price ?? event.price ?? 0;
                              const domText =
                                domesticPrice > 0
                                  ? `Rp ${domesticPrice.toLocaleString(
                                      "id-ID"
                                    )}`
                                  : "Free";
                              const foreText =
                                selectedCategory.foreignerPrice > 0
                                  ? `Rp ${selectedCategory.foreignerPrice.toLocaleString(
                                      "id-ID"
                                    )}`
                                  : "Free";

                              if (
                                domesticPrice === 0 &&
                                selectedCategory.foreignerPrice === 0
                              )
                                return "Free";
                              if (domesticPrice === 0)
                                return `Free / Foreigner ${foreText.replace(
                                  "Rp ",
                                  ""
                                )}`;
                              if (selectedCategory.foreignerPrice === 0)
                                return `${domText} / Foreigner Free`;

                              return `${domText} / Foreigner ${foreText.replace(
                                "Rp ",
                                ""
                              )}`;
                            })()}
                          </div>
                        ) : (
                          <div>
                            {(() => {
                              const price =
                                selectedCategory.price ?? event.price ?? 0;
                              return price > 0
                                ? `Rp ${price.toLocaleString("id-ID")}`
                                : "Free";
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Peserta */}
                    {event.eventId && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                          Participants
                        </p>

                        {/* SIMPLE: button is always rendered, but disabled and styled when isEntryListEnabled === false */}
                        <button
                          onClick={() => {
                            if (!isEntryListEnabled) return;
                            navigate(
                              `/entrylist/${event.eventId}/${
                                selectedCategory.distance || "all"
                              }`
                            );
                          }}
                          disabled={!isEntryListEnabled}
                          aria-disabled={!isEntryListEnabled}
                          className={`text-sm transition-colors ${
                            isEntryListEnabled
                              ? "text-red-600 hover:text-red-700"
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Entry List
                        </button>
                      </div>
                    )}

                    {/* GPX FILE */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        GPX FILE
                      </p>
                      {event.type === "trail_running" &&
                      selectedCategoryGpxSources.primaryUrl ? (
                        <button
                          className="text-red-600 font-medium flex items-center space-x-2 hover:text-red-700 transition-colors"
                          onClick={() =>
                            handleExportGpx(
                              selectedCategoryGpxSources,
                              selectedCategory,
                              event
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faFileDownload} />
                          <span>Download</span>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Not available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* STATUS, HARGA, PESERTA, NAVIGASI CONTAINER - MOBILE (unchanged) */}
              <div className="md:hidden border border-gray-300 rounded-2xl overflow-hidden">
                {/* Status */}
                <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Status
                  </p>
                  <div className="flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-2 ${
                        getStatusKey(event.status) === "open"
                          ? "bg-red-500"
                          : getStatusKey(event.status) === "closed"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {displayStatus}
                    </span>
                  </div>
                </div>

                {/* Harga - Mobile */}
                <div className="px-4 py-3 border-b border-gray-300 bg-white">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Price
                  </p>
                  <div className="text-sm font-semibold text-red-600">
                    {selectedCategory.foreignerPrice != null ? (
                      <div>
                        {(() => {
                          const domesticPrice =
                            selectedCategory.price ?? event.price ?? 0;
                          const domText =
                            domesticPrice > 0
                              ? `Rp ${domesticPrice.toLocaleString("id-ID")}`
                              : "Free";

                          const foreText =
                            selectedCategory.foreignerPrice > 0
                              ? `Rp ${selectedCategory.foreignerPrice.toLocaleString(
                                  "id-ID"
                                )}`
                              : "Free";

                          if (
                            domesticPrice === 0 &&
                            selectedCategory.foreignerPrice === 0
                          )
                            return "Free";
                          if (domesticPrice === 0)
                            return `Free / Foreigner ${foreText.replace(
                              "Rp ",
                              ""
                            )}`;
                          if (selectedCategory.foreignerPrice === 0)
                            return `${domText} / Foreigner Free`;

                          return `${domText} / Foreigner ${foreText.replace(
                            "Rp ",
                            ""
                          )}`;
                        })()}
                      </div>
                    ) : (
                      <div>
                        {(() => {
                          const price =
                            selectedCategory.price ?? event.price ?? 0;
                          return price > 0
                            ? `Rp ${price.toLocaleString("id-ID")}`
                            : "Free";
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Peserta */}
                {event.eventId && (
                  <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                      Participants
                    </p>

                    {/* MOBILE: same simple behavior — button disabled when not enabled */}
                    <button
                      onClick={() => {
                        if (!isEntryListEnabled) return;
                        navigate(
                          `/entrylist/${event.eventId}/${
                            selectedCategory.distance || "all"
                          }`
                        );
                      }}
                      disabled={!isEntryListEnabled}
                      aria-disabled={!isEntryListEnabled}
                      className={`text-sm transition-colors ${
                        isEntryListEnabled
                          ? "text-red-600 hover:text-red-700"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Entry List
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* DESKRIPSI KATEGORI */}
          <div className="text-slate-900">
            <div className="mx-auto max-w-7xl 2xl:max-w-[96rem] px-4">
              <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {event.aboutEvent || "No event description available."}
              </p>
            </div>
          </div>

          {event.type === "trail_running" && (
            <SectionJMMDetail selectedCategory={selectedCategory} />
          )}

          {/* TOMBOL DAFTAR */}
          <div className="mt-4">
            <div className="hidden md:block">
              <div className="flex items-center justify-end bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex justify-end space-x-0 divide-x divide-gray-300">
                  {registrationStatus === "registered" &&
                  transactionStatus === "settlement" ? (
                    <span className="flex-1 text-center uppercase px-20 py-4 bg-red-600 text-white whitespace-nowrap">
                      YOU ARE REGISTERED
                    </span>
                  ) : registrationStatus === "registered" &&
                    transactionStatus === "pending" ? (
                    <button
                      onClick={handleBayar}
                      className="flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 bg-red-600 font-normal text-white whitespace-nowrap hover:bg-red-800"
                      disabled={!isSnapReady || !snapToken}
                    >
                      COMPLETE PAYMENT
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(selectedCategory)}
                      className={`flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 whitespace-nowrap ${
                        isRegistrationOpen
                          ? "bg-red-600 font-normal text-white hover:bg-red-800"
                          : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                      }`}
                      disabled={!isRegistrationOpen}
                    >
                      {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="md:hidden">
              <div className="bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300">
                  <div>
                    {registrationStatus === "registered" &&
                    transactionStatus === "settlement" ? (
                      <span className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap bg-red-600 text-white">
                        YOU ARE REGISTERED
                      </span>
                    ) : registrationStatus === "registered" &&
                      transactionStatus === "pending" ? (
                      <button
                        onClick={handleBayar}
                        className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 bg-red-600 font-normal text-white whitespace-nowrap hover:bg-red-800"
                        disabled={!isSnapReady || !snapToken}
                      >
                        COMPLETE PAYMENT
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRegister(selectedCategory)}
                        className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-red-600 font-normal text-white hover:bg-red-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {imageModal.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setImageModal({ isOpen: false, src: "", alt: "" })}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <img
              src={imageModal.src}
              alt={imageModal.alt}
              className="w-full h-full object-contain max-h-[90vh]"
            />
          </div>
        </div>
      )}
    </>
  );
});

export default JMM;
